import { openDB } from "idb";
import { azanVoice } from "./azanVoices";

// The household's own azan recordings, picked from files on the tablet. They stay on this tablet
// and are never uploaded.
export type OwnAzanSlot = "main" | "fajr";

export const OWN_VOICE = "own";

const MAX_BYTES = 20 * 1024 * 1024;
const MAX_SECONDS = 10 * 60;

const db = () =>
  openDB("m247-home-azan", 1, {
    upgrade: (database) => {
      database.createObjectStore("recordings");
    },
  });

const load = async (slot: OwnAzanSlot): Promise<File | undefined> =>
  (await db()).get("recordings", slot).catch(() => undefined);

// One object URL per recording for the session, so playing it five times a day doesn't leak.
const urls: Partial<Record<OwnAzanSlot, string | null>> = {};

const forget = (slot: OwnAzanSlot) => {
  const url = urls[slot];
  if (url) URL.revokeObjectURL(url);
  delete urls[slot];
};

const ownAzanUrl = async (slot: OwnAzanSlot): Promise<string | null> => {
  if (!(slot in urls)) {
    const file = await load(slot);
    urls[slot] = file ? URL.createObjectURL(file) : null;
  }
  return urls[slot] ?? null;
};

export const ownAzanName = async (slot: OwnAzanSlot): Promise<string | null> =>
  (await load(slot))?.name ?? null;

export const saveOwnAzan = async (slot: OwnAzanSlot, file: File) => {
  await (await db()).put("recordings", file, slot);
  forget(slot);
};

export const removeOwnAzan = async (slot: OwnAzanSlot) => {
  await (await db()).delete("recordings", slot);
  forget(slot);
};

export const clearOwnAzan = () => Promise.all([removeOwnAzan("main"), removeOwnAzan("fajr")]);

// The household's Fajr recording at Fajr, their own recording if chosen, otherwise the built-in
// voice. A missing recording falls back to a built-in voice, never to silence.
export const azanSource = async (voiceId: string, fajr: boolean): Promise<string> =>
  (fajr && (await ownAzanUrl("fajr"))) ||
  (voiceId === OWN_VOICE && (await ownAzanUrl("main"))) ||
  azanVoice(voiceId).file;

// Null when the file is fine, otherwise what to tell the household.
export const checkRecording = (file: File): Promise<string | null> => {
  if (file.size > MAX_BYTES) return Promise.resolve("That file is too big. Choose one under 20 MB.");
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const sound = new Audio();
    const done = (problem: string | null) => {
      window.clearTimeout(timer);
      URL.revokeObjectURL(url);
      resolve(problem);
    };
    const cantPlay = "This tablet can't play that file. Try an MP3.";
    const timer = window.setTimeout(() => done(cantPlay), 10_000);
    sound.onloadedmetadata = () =>
      done(
        Number.isFinite(sound.duration) && sound.duration > MAX_SECONDS
          ? "Choose just the azan, under 10 minutes."
          : null,
      );
    sound.onerror = () => done(cantPlay);
    sound.preload = "metadata";
    sound.src = url;
  });
};
